import { GET_MEASUREMENT_STATIONS } from "utils/stations";
import { GET_SENSORS } from "utils/sensors";

export interface NativeAppMessage {
  type: string;
  payload?: unknown;
}

export interface LocalReportSummary {
  id: string;
  status: string;
  reportType: string;
  stationId: number | null;
  stationName: string | null;
  organizationName: string | null;
  responsiblePerson: string | null;
  timestamp: string | null;
  updatedAt: number;
  attachmentCount: number;
}

export interface LocalReportPayload {
  id: string;
  status: string;
  reportType: string;
  stationId: number | null;
  responsiblePerson: string | null;
  timestamp: string | null;
  data: Record<string, unknown>;
  attachments: {
    filename: string;
    mimeType: string;
    base64: string;
    isAdminOnly?: boolean;
  }[];
}

const MOBILE_SESSION_TOKEN_KEY = "mgSessionToken";

function maskToken(value: string | undefined | null): string | undefined {
  if (!value || value.length <= 8) return value ?? undefined;
  return value.slice(0, 4) + "****" + value.slice(-4);
}

function maskMessagePayload(type: string, payload: unknown): unknown {
  if (type === "SESSION_TOKEN" && payload && typeof payload === "object") {
    const p = payload as { token?: string };
    return { token: maskToken(p.token) };
  }
  return payload;
}

class NativeAppBridge {
  private apolloClient: any;
  private localReportsCallback:
    | ((reports: LocalReportSummary[]) => void)
    | null = null;
  private localReportsErrorCallback: ((message: string) => void) | null = null;
  private reportPayloadCallback:
    | ((payload: LocalReportPayload) => void)
    | null = null;
  private reportPayloadErrorCallback: ((message: string) => void) | null = null;

  isNativeConnected: boolean = false;
  private connectedCallback: (() => void) | null = null;

  onConnected(callback: () => void) {
    if (this.isNativeConnected) {
      callback();
    } else {
      this.connectedCallback = callback;
    }
  }

  setApolloClient(apolloClient: any) {
    this.apolloClient = apolloClient;
  }

  sendMessage(type: string, payload?: unknown) {
    if (!window.parent) {
      console.warn("[NativeAppBridge] Cannot send message: no parent window");
      return;
    }

    const message: NativeAppMessage = { type, payload };
    window.parent.postMessage(message, "*");

    const logPayload = maskMessagePayload(type, payload);
    console.log("[NativeAppBridge] Sent message:", {
      type,
      payload: logPayload,
    });
  }

  handleMessage(event: MessageEvent) {
    console.log("[NativeAppBridge] Received message event:", event);

    // TODO: consider restricting to capacitor's origin
    // const origin = window.location.origin;
    // if (event.origin !== origin) {
    //   console.warn(
    //     "[NativeAppBridge] Ignored message from unauthorized origin:",
    //     event.origin,
    //   );
    //   return;
    // }

    const { type, payload } = event.data as NativeAppMessage;
    const logPayload = maskMessagePayload(type, payload);
    console.log("[NativeAppBridge] Received message:", {
      type,
      payload: logPayload,
    });

    switch (type) {
      case "NATIVE_STANDBY":
        this.isNativeConnected = true;
        console.log(
          "[NativeAppBridge] Native app is in standby mode. this.isNativeConnected =",
          this.isNativeConnected,
        );
        break;
      case "NATIVE_READY":
        console.log(
          "[NativeAppBridge] Native app is ready. this.isNativeConnected =",
          this.isNativeConnected,
        );
        this.isNativeConnected = true;
        if (this.connectedCallback) {
          this.connectedCallback();
          this.connectedCallback = null;
        }
        break;
      case "GET_STATIC_RESOURCES":
        this.loadStaticResources();
        break;
      case "GET_SESSION_TOKEN":
        this.sendSessionToken();
        break;
      case "LOCAL_REPORTS_LIST":
        if (this.localReportsCallback) {
          this.localReportsCallback(payload as LocalReportSummary[]);
          this.localReportsCallback = null;
          this.localReportsErrorCallback = null;
        }
        break;
      case "LOCAL_REPORTS_ERROR":
        if (this.localReportsErrorCallback) {
          this.localReportsErrorCallback(
            (payload as { message: string })?.message ?? "Unknown error",
          );
          this.localReportsCallback = null;
          this.localReportsErrorCallback = null;
        }
        break;
      case "REPORT_PAYLOAD":
        if (this.reportPayloadCallback) {
          this.reportPayloadCallback(payload as LocalReportPayload);
          this.reportPayloadCallback = null;
          this.reportPayloadErrorCallback = null;
        }
        break;
      case "REPORT_PAYLOAD_ERROR":
        if (this.reportPayloadErrorCallback) {
          this.reportPayloadErrorCallback(
            (payload as { message: string })?.message ?? "Unknown error",
          );
          this.reportPayloadCallback = null;
          this.reportPayloadErrorCallback = null;
        }
        break;
      default:
        if (type.startsWith("webpack")) {
          // Ignore webpack's internal messages
          return;
        }
        console.log("[NativeAppBridge] Unhandled message type:", type);
    }
  }

  startListening() {
    this.handleMessage = this.handleMessage.bind(this);
    window.addEventListener("message", this.handleMessage);
    return () => {
      window.removeEventListener("message", this.handleMessage);
    };
  }

  async loadStaticResources() {
    const { data: stationData } = await this.runApolloQuery(
      GET_MEASUREMENT_STATIONS,
    );
    const { data: sensorData } = await this.runApolloQuery(GET_SENSORS);

    const resources: any = {};
    const stations = stationData?.station ?? [];
    const sensors = sensorData?.sensor ?? [];
    resources["stations"] = stations.map((s: any) => ({
      id: s.id,
      identifier: s.identifier,
      name: s.name,
      organization: s.organization
        ? {
            name: s.organization.name,
          }
        : null,
      lat: s.lat,
      lon: s.lon,
    }));
    resources["sensors"] = sensors.map((s: any) => ({
      id: s.id,
      name: s.name,
    }));

    this.sendMessage("STATIC_RESOURCES_LOADED", resources);
  }

  sendSessionToken() {
    const token = window.localStorage.getItem(MOBILE_SESSION_TOKEN_KEY);
    this.sendMessage("SESSION_TOKEN", { token });
  }

  notifyLogout() {
    this.sendMessage("USER_LOGGED_OUT");
  }

  proactivelySendSessionToken() {
    const token = window.localStorage.getItem(MOBILE_SESSION_TOKEN_KEY);
    if (token && this.isNativeConnected) {
      this.sendMessage("SESSION_TOKEN", { token });
    }
  }
  requestLocalReports(): Promise<LocalReportSummary[]> {
    return new Promise((resolve, reject) => {
      this.localReportsCallback = resolve;
      this.localReportsErrorCallback = reject;
      this.sendMessage("GET_LOCAL_REPORTS");
    });
  }

  requestReportPayload(id: string): Promise<LocalReportPayload> {
    return new Promise((resolve, reject) => {
      this.reportPayloadCallback = resolve;
      this.reportPayloadErrorCallback = reject;
      this.sendMessage("GET_REPORT_PAYLOAD", { id });
    });
  }

  async runApolloQuery(
    query: any,
    variables?: any,
    fetchPolicy: string = "network-only",
  ) {
    try {
      const result = await this.apolloClient.query({
        query,
        fetchPolicy,
        variables,
      });
      return result;
    } catch (error) {
      console.error("[NativeAppBridge] Apollo query error:", error);
      throw error;
    }
  }
}

export const nativeAppBridge = new NativeAppBridge();
